// src/scenes/store/CreateStore.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import SimpleForm from "../../components/SimpleForm";
import { Snackbar, Alert, CircularProgress, Box } from "@mui/material";

/* ---------- axios ---------- */
const token = localStorage.getItem("token");

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
});

/* ---------- helpers ---------- */
const buildInitials = (fields) =>
  fields.reduce((acc, f) => {
    const def =
      f.default ??
      (f.type === "checkbox"
        ? false
        : f.type === "number"
        ? ""
        : f.type === "date"
        ? ""
        : "");
    acc[f.name] = def;
    return acc;
  }, {});

const buildSchema = (fields) => {
  const shape = {};
  fields.forEach((f) => {
    let rule;
    switch (f.type) {
      case "number":
        rule = yup.number().typeError("must be a number");
        break;
      case "checkbox":
        rule = yup.boolean();
        break;
      case "date":
        rule = yup.date().typeError("invalid date");
        break;
      case "select":
        rule = yup.string();
        break;
      default:
        rule = yup.string();
    }
    if (f.required) rule = rule.required("required");
    if (f.min != null) rule = rule.min(f.min);
    if (f.max != null) rule = rule.max(f.max);
    shape[f.name] = rule;
  });
  return yup.object().shape(shape);
};

async function postJSON(path, data) {
  try {
    const res = await api.post(path, data);
    return { data: res.data, error: null };
  } catch (err) {
    const serverErrors =
      err?.response?.data?.errors ||
      err?.response?.data?.message ||
      err.message;
    return { data: null, error: serverErrors };
  }
}

/* ---------- page ---------- */
export default function CreateStore() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [loadingOpts, setLoadingOpts] = useState(true);
  const [itemOptions, setItemOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [lastRefreshed, setLastRefreshed] = useState(Date.now());

  // Load items & warehouses (callable + cache-busting)
  const loadOptions = useCallback(async () => {
    setLoadingOpts(true);
    const t = Date.now();
    try {
      const [itemsRes, warehousesRes] = await Promise.all([
        api.get("/api/item", {
          params: { t },
          
        }),
        api.get("/api/warehouse", {
          params: { t },
          
        }),
      ]);

     const items = itemsRes?.data?.data ?? [];
const warehouses = warehousesRes?.data?.data ?? [];

const itOpts = items
  .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
  .map((i) => ({ value: String(i.id), label: i.name }));

const whOpts = warehouses
  .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
  .map((w) => ({ value: String(w.id), label: w.name }));

      setItemOptions(itOpts);
      setWarehouseOptions(whOpts);
      setLastRefreshed(Date.now()); // force remount of form/fields
    } catch (e) {
      setToast({
        open: true,
        msg: "Failed to load items/warehouses",
        sev: "error",
      });
    } finally {
      setLoadingOpts(false);
    }
  }, []);

  // Load once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await loadOptions();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadOptions]);

  // Refresh when the window regains focus
  useEffect(() => {
    const onFocus = () => loadOptions();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadOptions]);

  // Optional: allow other screens to trigger refresh
  useEffect(() => {
    const handler = () => loadOptions();
    window.addEventListener("store:refresh-master-data", handler);
    return () => window.removeEventListener("store:refresh-master-data", handler);
  }, [loadOptions]);

  const FIELDS = useMemo(
    () => [
      {
        name: "itemId",
        label: "Item",
        type: "select",
        span: 2,
        required: true,
        options: itemOptions,
        key: `item-${lastRefreshed}`,
        props: {
          disabled: loadingOpts || itemOptions.length === 0,
          placeholder: loadingOpts ? "Loading..." : "Choose item",
        },
      },
      {
        name: "warehouseId",
        label: "Warehouse",
        type: "select",
        span: 2,
        required: true,
        options: warehouseOptions,
        key: `warehouse-${lastRefreshed}`,
        props: {
          disabled: loadingOpts || warehouseOptions.length === 0,
          placeholder: loadingOpts ? "Loading..." : "Choose warehouse",
        },
      },
      {
        name: "quantity",
        label: "Quantity",
        type: "number",
        span: 2,
        required: true,
        min: 0,
        props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
      },
    ],
    [itemOptions, warehouseOptions, loadingOpts, lastRefreshed]
  );

  const INITIALS = useMemo(() => buildInitials(FIELDS), [FIELDS]);
  const SCHEMA = useMemo(() => buildSchema(FIELDS), [FIELDS]);

  const handleSubmit = async (values, { setSubmitting, setErrors, resetForm }) => {
    const payload = {
      itemId: values.itemId ? Number(values.itemId) : undefined,
      warehouseId: values.warehouseId ? Number(values.warehouseId) : undefined,
      quantity: Number(values.quantity),
    };

    const { data, error } = await postJSON("/api/store", payload);
    setSubmitting(false);

    if (error) {
      if (typeof error === "object" && !Array.isArray(error)) setErrors(error);
      setToast({ open: true, msg: "Failed to create store record", sev: "error" });
      return;
    }

    const msg = data?.message || "Store record created successfully";
    setToast({ open: true, msg, sev: "success" });
    resetForm();

    // Refresh dropdowns after successful create
    await loadOptions();
  };

  const LoadingBar = loadingOpts ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <CircularProgress size={18} />
      <span style={{ fontSize: 14, opacity: 0.8 }}>Loading dropdowns…</span>
    </Box>
  ) : null;

  // Force SimpleForm to re-render when options change
  const formKey = `${lastRefreshed}`;

  return (
    <>
      <SimpleForm
        key={formKey}
        title="CREATE STORE RECORD"
        subtitle="Move item quantity into a warehouse"
        Header={Header}
        fields={FIELDS}
        initialValues={INITIALS}
        validation={SCHEMA}
        onSubmit={handleSubmit}
        submitLabel="Create Store Record"
        columns={4}
      />

      {LoadingBar}

      <Snackbar
       anchorOrigin={{ vertical: 'top', horizontal: 'right'}}
      sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
